import { supabase } from './supabase';
import { Tables } from '../types/database.types';

export type Contestant = Tables<'contestants'>;
export type Season = Tables<'seasons'>;

export interface ContestantWithSeason extends Contestant {
  season?: Season & { reality?: { name: string } };
}

export const contestantsService = {
  // Obtener todos los concursantes con información de la temporada
  async getAll(): Promise<ContestantWithSeason[]> {
    const { data, error } = await supabase
      .from('contestants')
      .select(`
        *,
        season:seasons(
          *,
          reality:realities(name)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Obtener concursantes por temporada
  async getBySeason(seasonId: string): Promise<ContestantWithSeason[]> {
    const { data, error } = await supabase
      .from('contestants')
      .select(`
        *,
        season:seasons(
          *,
          reality:realities(name)
        )
      `)
      .eq('season_id', seasonId)
      .order('full_name', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Obtener un concursante por ID
  async getById(id: string): Promise<ContestantWithSeason | null> {
    const { data, error } = await supabase
      .from('contestants')
      .select(`
        *,
        season:seasons(
          *,
          reality:realities(name)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Crear un nuevo concursante
  async create(contestant: Omit<Contestant, 'id' | 'created_at' | 'updated_at'>): Promise<Contestant> {
    const { data, error } = await supabase
      .from('contestants')
      .insert(contestant)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Actualizar un concursante
  async update(id: string, contestant: Partial<Omit<Contestant, 'id' | 'created_at' | 'updated_at'>>): Promise<Contestant> {
    const { data, error } = await supabase
      .from('contestants')
      .update({ ...contestant, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Actualizar estado de un concursante
  async updateStatus(
    id: string,
    status: 'active' | 'eliminated' | 'finalist' | 'winner',
    eliminationDate?: string,
    finalPosition?: number
  ): Promise<Contestant> {
    const updates: any = {
      status,
      updated_at: new Date().toISOString()
    };

    if (eliminationDate) updates.elimination_date = eliminationDate;
    if (finalPosition !== undefined) updates.final_position = finalPosition;

    const { data, error } = await supabase
      .from('contestants')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Eliminar un concursante
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('contestants')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
